import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import { HallOfFameEntry, HallType } from './ranking';
import { Nullable, UserWithToken, WithData } from './api.types';

export const getHallOfFame = async (
  user: Nullable<UserWithToken>,
  tournamentType?: HallType
) =>
  getGraphQLQueryClient(user).request<WithData<HallOfFameEntry[]>>(
    gql`
      query hallOfFame($tournamentType: HallOfFameTournamentType) {
        data: hallOfFame(tournamentType: $tournamentType) {
          id
          name
          disabled
          tournamentId
          tournament {
            id
            swissId
          }
          winner {
            name
            secondName
            lastName
            secondLastName
            cossyId
            deck
            decklist
            userId
            position
            user {
              id
              picture
            }
          }
          topPlayers {
            name
            secondName
            lastName
            secondLastName
            cossyId
            deck
            decklist
            userId
            position
          }
          date
        }
      }
    `,
    { tournamentType }
  );

export const getHallOfFameEntry = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<HallOfFameEntry>>(
    gql`
      query hallOfFameEntry($id: Int!) {
        data: hallOfFameEntry(id: $id) {
          id
          name
          date
          disabled
          tournamentId
          winner {
            name
            secondName
            lastName
            secondLastName
            cossyId
            deck
            decklist
            userId
            position
            user {
              id
              name
              secondName
              lastName
              secondLastName
              picture
              cossyId
            }
            parsedDecklist {
              main {
                konamiId
                quantity
                card {
                  konamiId
                  name
                  image
                }
              }
              extra {
                konamiId
                quantity
                card {
                  konamiId
                  name
                  image
                }
              }
              side {
                konamiId
                quantity
                card {
                  konamiId
                  name
                  image
                }
              }
            }
          }
          topPlayers {
            name
            secondName
            lastName
            secondLastName
            cossyId
            deck
            decklist
            userId
            position
            user {
              id
              name
              secondName
              lastName
              secondLastName
              picture
              cossyId
            }
            parsedDecklist {
              main {
                konamiId
                quantity
                card {
                  konamiId
                  name
                  image
                }
              }
              extra {
                konamiId
                quantity
                card {
                  konamiId
                  name
                  image
                }
              }
              side {
                konamiId
                quantity
                card {
                  konamiId
                  name
                  image
                }
              }
            }
          }
        }
      }
    `,
    { id }
  );

/**
 * Un cupo del podio: una posición y la cuenta que la ocupa.
 *
 * El nombre, el cossy y el mazo NO se mandan: se derivan de la cuenta y del
 * torneo cada vez que se leen. Mandarlos era lo que permitía que la entrada
 * mostrara una identidad distinta de la del usuario linkeado.
 */
export type HallOfFamePlayerInput = {
  userId: number;
  position?: number;
};

export type CreateHallOfFameEntryInput = {
  tournamentType: HallType;
  name: string;
  date: string;
  winner: HallOfFamePlayerInput;
  topPlayers?: HallOfFamePlayerInput[];
  /** Obligatorio: entrar al Salón de la Fama exige el torneo. */
  tournamentId: number;
};

export type UpdateHallOfFameEntryInput = {
  tournamentType: HallType;
  id: number;
  name?: string;
  date?: string;
  winner?: HallOfFamePlayerInput;
  topPlayers?: HallOfFamePlayerInput[];
  disabled?: boolean;
  tournamentId?: number | null;
};

export const createHallOfFameEntry = async (
  user: Nullable<UserWithToken>,
  input: CreateHallOfFameEntryInput
) =>
  getGraphQLQueryClient(user).request<WithData<HallOfFameEntry>>(
    gql`
      mutation createHallOfFameEntry($input: CreateHallOfFameEntryInput!) {
        data: createHallOfFameEntry(input: $input) {
          id
          name
          date
          disabled
          tournamentId
          winner {
            name
            secondName
            lastName
            secondLastName
            cossyId
            deck
            decklist
            userId
            position
          }
          topPlayers {
            name
            secondName
            lastName
            secondLastName
            cossyId
            deck
            decklist
            userId
            position
          }
        }
      }
    `,
    { input }
  );

export const updateHallOfFameEntry = async (
  user: Nullable<UserWithToken>,
  input: UpdateHallOfFameEntryInput
) =>
  getGraphQLQueryClient(user).request<WithData<HallOfFameEntry>>(
    gql`
      mutation updateHallOfFameEntry($input: UpdateHallOfFameEntryInput!) {
        data: updateHallOfFameEntry(input: $input) {
          id
          name
          date
          disabled
          tournamentId
          winner {
            name
            secondName
            lastName
            secondLastName
            cossyId
            deck
            decklist
            userId
            position
          }
          topPlayers {
            name
            secondName
            lastName
            secondLastName
            cossyId
            deck
            decklist
            userId
            position
          }
        }
      }
    `,
    { input }
  );

export const deleteHallOfFameEntry = async (
  user: Nullable<UserWithToken>,
  tournamentType: HallType,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deleteHallOfFameEntry(
        $tournamentType: HallOfFameTournamentType!
        $id: Int!
      ) {
        data: deleteHallOfFameEntry(tournamentType: $tournamentType, id: $id)
      }
    `,
    { tournamentType, id }
  );

export const sendTournamentToHallOfFame = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  tournamentType: HallType,
  topCount?: number
) =>
  getGraphQLQueryClient(user).request<WithData<HallOfFameEntry>>(
    gql`
      mutation sendTournamentToHallOfFame(
        $tournamentId: Int!
        $tournamentType: HallOfFameTournamentType!
        $topCount: Int
      ) {
        data: sendTournamentToHallOfFame(
          tournamentId: $tournamentId
          tournamentType: $tournamentType
          topCount: $topCount
        ) {
          id
          name
          date
          disabled
          tournamentId
          winner {
            name
            secondName
            lastName
            secondLastName
            cossyId
            deck
            decklist
            userId
            position
          }
          topPlayers {
            name
            secondName
            lastName
            secondLastName
            cossyId
            deck
            decklist
            userId
            position
          }
        }
      }
    `,
    { tournamentId, tournamentType, topCount }
  );

export type PreregisterHallOfFameResult = {
  qualifiers: number;
  enrolled: number;
  alreadyEnrolled: number;
  failed: number;
};

export const preregisterHallOfFameTop = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  tournamentType: HallType,
  topCount?: number
) =>
  getGraphQLQueryClient(user).request<WithData<PreregisterHallOfFameResult>>(
    gql`
      mutation preregisterHallOfFameTop(
        $tournamentId: Int!
        $tournamentType: HallOfFameTournamentType!
        $topCount: Int
      ) {
        data: preregisterHallOfFameTop(
          tournamentId: $tournamentId
          tournamentType: $tournamentType
          topCount: $topCount
        ) {
          qualifiers
          enrolled
          alreadyEnrolled
          failed
        }
      }
    `,
    { tournamentId, tournamentType, topCount }
  );

export type PreregisterLeagueWinnersResult = {
  winners: number;
  enrolled: number;
  alreadyEnrolled: number;
  failed: number;
  notFinished: number;
};

export const preregisterLeagueWinners = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<PreregisterLeagueWinnersResult>>(
    gql`
      mutation preregisterLeagueWinners($tournamentId: Int!) {
        data: preregisterLeagueWinners(tournamentId: $tournamentId) {
          winners
          enrolled
          alreadyEnrolled
          failed
          notFinished
        }
      }
    `,
    { tournamentId }
  );

export type PreregisterTeamQualifiersResult = {
  teams: number;
  qualifiers: number;
  enrolled: number;
  alreadyEnrolled: number;
  failed: number;
  notFinished: number;
};

export const preregisterTeamQualifiers = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  topCount?: number
) =>
  getGraphQLQueryClient(user).request<
    WithData<PreregisterTeamQualifiersResult>
  >(
    gql`
      mutation preregisterTeamQualifiers($tournamentId: Int!, $topCount: Int) {
        data: preregisterTeamQualifiers(
          tournamentId: $tournamentId
          topCount: $topCount
        ) {
          teams
          qualifiers
          enrolled
          alreadyEnrolled
          failed
          notFinished
        }
      }
    `,
    { tournamentId, topCount }
  );
